import {
  type SearchPagination,
  type SearchResponse,
} from '@algolia/client-search';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useLocation, useMatches } from 'react-router';

import { configString } from '#/config/appConfigUtils';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { isSearchLandingPageSSRMatch } from '#/modules/cms/routing/isSearchLandingPageSSRMatch';
import { useEmbedSearchWidgetLayoutRouteLoaderData } from '#/modules/embedSearchWidget/hooks/useEmbedSearchWidgetLayoutRouteLoaderData';
import { useIsInEmbedSearchWidget } from '#/modules/embedSearchWidget/hooks/useIsInEmbedSearchWidget';
import { EMBED_SEARCH_WIDGET_VOLOP_SOURCES_ALLOWED } from '#/modules/embedSearchWidget/volops/search/embedSearchWidgetVolopSearchConstants';
import type { ApiVolopPromotionSelected } from '#/modules/listing/volops/api/types/ApiVolopPromotionSelected';
import { searchAlgoliaOptionsLocationData } from '#/modules/search/algolia/options/searchAlgoliaOptionsLocationData';
import { searchAlgoliaOptionsWithSearchLocation } from '#/modules/search/algolia/options/searchAlgoliaOptionsWithSearchLocation';
import { searchAlgoliaOptionsSwitch } from '#/modules/search/algolia/options/switch/searchAlgoliaOptionsSwitch';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaIndexSwitch } from '#/modules/search/algolia/searchAlgoliaIndexSwitch';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import type { SearchAlgoliaVolopHit } from '#/modules/search/algolia/types/SearchAlgoliaVolopHit';
import { searchApiFetchPromotedVolop } from '#/modules/search/api/searchApiFetchPromotedVolop';
import { SEARCH_REFINEMENT_NAMES } from '#/modules/search/constants/refinements/searchRefinementTypes';
import { extractLocationAnalyticsProperties } from '#/modules/search/helpers/analytics/extractLocationAnalyticsProperties';
import { radiusIntToString } from '#/modules/search/helpers/radius';
import { isLocationEmpty } from '#/modules/search/helpers/searchIsLocationEmpty';
import type { SearchFiltersByName } from '#/modules/search/types/FiltersByName/SearchFiltersByName';
import type { SearchFacetId } from '#/modules/search/types/SearchFacet';
import type { SearchFacetCounts } from '#/modules/search/types/SearchFacetCounts';
import type { SearchFiltersById } from '#/modules/search/types/SearchFiltersById';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchSortType } from '#/modules/search/types/SearchSortType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { algoliaSearchMulti } from '#/utils/algolia/algoliaSearchMulti';
import { algoliaBuildFilterString } from '#/utils/algolia/helpers/algoliaBuildFilterString';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { EMPTY_SEARCH_LOCATION } from '#/utils/constants/general/emptySearchLocation';
import { SEARCH_RADIUSES } from '#/utils/constants/general/searchRadiuses';
import { setItem } from '#/utils/localStorage';
import { getListingTypeFromPathname } from '#/utils/search/getListingTypeFromPathname';
import { createZustandContextStore } from '#/zustand-stores/utils/createZustandContextStore';

function parseRadius(radius: string, locale: I18nLocale) {
  const isValidRadius = SEARCH_RADIUSES[locale][radius];

  if (isValidRadius) return radius;
}

type SearchContextState = {
  query: string;
  pageIndex: number;
  resultsByPage: Partial<
    Record<
      string,
      SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>
    >
  >;
  isSearching: boolean;
  sort: SearchSortType;
  facetCounts: SearchFacetCounts | undefined;
  filters: SearchFiltersById;
  filtersByName: SearchFiltersByName;
  searchLocation: SearchLocation | undefined;
  radius: string | undefined;
  promotedListing: {
    volopHit: SearchAlgoliaVolopHit;
    promotionSelected: ApiVolopPromotionSelected;
  } | null;
};

const emptyState: SearchContextState = {
  query: '',
  facetCounts: undefined,
  pageIndex: 0,
  resultsByPage: {},
  isSearching: false,
  sort: 'relevance',
  filters: { type: 'JOB' },
  filtersByName: { type: 'JOB' },
  searchLocation: undefined,
  radius: undefined,
  promotedListing: null,
};

const {
  ZustandContextProvider,
  useZustandContextStore,
  useZustandContextSelector,
} = createZustandContextStore<SearchContextState>({
  fallbackState: emptyState,
});

export function SearchContextProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const matches = useMatches();
  const location = useLocation();

  const searchLandingPageMatch = matches.find(isSearchLandingPageSSRMatch);
  const { embedSearchWidgetSearchLocation, embedSearchWidgetSearchRadius } =
    useEmbedSearchWidgetLayoutRouteLoaderData();

  const listingTypeFromPath = getListingTypeFromPathname(location.pathname);

  const initialState: SearchContextState = useMemo(() => {
    const baseEmptyState = {
      ...emptyState,
      filters: { type: listingTypeFromPath },
      filtersByName: { type: listingTypeFromPath },
    };

    if (embedSearchWidgetSearchLocation) {
      return {
        ...baseEmptyState,
        radius:
          radiusIntToString(
            embedSearchWidgetSearchRadius,
            embedSearchWidgetSearchLocation?.geoType,
          ) ?? '',
      };
    }

    if (!searchLandingPageMatch?.loaderData?.ssr) return baseEmptyState;

    const {
      content,
      ssr: { results },
    } = searchLandingPageMatch.loaderData;

    const pageIndex = results.page;

    return {
      query: content.searchText ?? '',
      pageIndex,
      facetCounts: undefined,
      resultsByPage: { [pageIndex]: results },
      isSearching: false,
      sort: 'relevance',
      filters: content.filters,
      filtersByName: content.filters,
      searchLocation: content.location.searchLocation,
      radius: content.location.radius
        ? parseRadius(content.location.radius.toString(), locale)
        : undefined,
      promotedListing: null,
    };
  }, [
    locale,
    searchLandingPageMatch,
    embedSearchWidgetSearchLocation,
    embedSearchWidgetSearchRadius,
    listingTypeFromPath,
  ]);

  return (
    <ZustandContextProvider
      key={searchLandingPageMatch ? searchLandingPageMatch.pathname : undefined}
      initialState={initialState}
    >
      {children}
    </ZustandContextProvider>
  );
}

// Hooks

export function useSearchGetters() {
  const searchStore = useZustandContextStore();

  const getSearchFilterType = useCallback((): SearchType => {
    return searchStore.getState().filters.type;
  }, [searchStore]);

  const getSearchCurrentPageResults = useCallback(():
    | (SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>)
    | undefined => {
    const { pageIndex, resultsByPage } = searchStore.getState();
    return resultsByPage[pageIndex];
  }, [searchStore]);

  const getSearchQuery = useCallback((): string => {
    return searchStore.getState().query;
  }, [searchStore]);

  const getSearchLocation = useCallback((): SearchLocation | undefined => {
    return searchStore.getState().searchLocation;
  }, [searchStore]);

  const getSearchFiltersById = useCallback((): SearchFiltersById => {
    return searchStore.getState().filters;
  }, [searchStore]);

  const getSearchFiltersByName = useCallback(() => {
    return searchStore.getState().filtersByName;
  }, [searchStore]);

  const getSearchSort = useCallback((): SearchSortType => {
    return searchStore.getState().sort;
  }, [searchStore]);

  const getSearchRadius = useCallback((): string => {
    const { radius } = searchStore.getState();
    return radius ?? '';
  }, [searchStore]);

  const getSearchPageIndex = useCallback((): number => {
    return searchStore.getState().pageIndex;
  }, [searchStore]);

  const selectedOption = useCallback(
    (facetId: SearchFacetId) => {
      const options = getSearchFiltersById()[facetId];
      return Array.isArray(options) ? options[0] : options;
    },
    [getSearchFiltersById],
  );

  const getSearchPromotedListing = useCallback((): {
    volopHit: SearchAlgoliaVolopHit;
    promotionSelected: ApiVolopPromotionSelected;
  } | null => {
    return searchStore.getState().promotedListing;
  }, [searchStore]);

  return {
    getSearchFilterType,
    getSearchCurrentPageResults,
    getSearchQuery,
    getSearchLocation,
    getSearchFiltersById,
    getSearchFiltersByName,
    getSearchSort,
    getSearchRadius,
    getSearchPageIndex,
    getSearchPromotedListing,
    selectedOption,
  };
}

export function useSearchFilterType(): SearchType {
  return useZustandContextSelector((state) => state.filters.type);
}

export function useSearchResultsByPage(): Partial<
  Record<number, SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>>
> {
  return useZustandContextSelector((state) => state.resultsByPage);
}

export function useSearchCurrentPageResults<
  THitType extends SearchAlgoliaHit = SearchAlgoliaHit,
>(): (SearchResponse<THitType> & Required<SearchPagination>) | undefined {
  return useZustandContextSelector(
    (state) => state.resultsByPage[state.pageIndex],
  ) as (SearchResponse<THitType> & Required<SearchPagination>) | undefined;
}

export function useSearchQuery(): string {
  return useZustandContextSelector((state) => state.query);
}

export function useSearchLocation(): SearchLocation | undefined {
  return useZustandContextSelector((state) => state.searchLocation);
}

export function useSearchLocationOrEmpty(): SearchLocation {
  return useZustandContextSelector(
    (state) => state.searchLocation ?? EMPTY_SEARCH_LOCATION,
  );
}

export function useSearchFiltersById(): SearchFiltersById {
  return useZustandContextSelector((state) => state.filters);
}

export function useSearchFiltersByName() {
  return useZustandContextSelector((state) => state.filtersByName);
}

export function useSearchSort(): SearchSortType {
  return useZustandContextSelector((state) => state.sort);
}

export function useIsSearching(): boolean {
  return useZustandContextSelector((state) => state.isSearching);
}

export function useSearchRadius(): string {
  return useZustandContextSelector(({ radius }) => radius ?? '');
}

export function useSearchPageIndex(): number {
  return useZustandContextSelector((state) => state.pageIndex);
}

export function useSearchFacetCounts(): SearchFacetCounts | undefined {
  return useZustandContextSelector((state) => state.facetCounts);
}

// New

let lastSearchTimestamp: number | undefined;
export function useSearchActions() {
  const { i18n } = useI18n();

  const searchStore = useZustandContextStore();
  const {
    getSearchQuery,
    getSearchFilterType,
    getSearchLocation,
    getSearchFiltersById,
    getSearchFiltersByName,
    getSearchSort,
    getSearchRadius,
    getSearchPageIndex,
  } = useSearchGetters();

  // Check if we're in an embed widget context
  const { isInEmbedSearchWidget } = useIsInEmbedSearchWidget();

  const updateSearchLocation = useCallback(
    (searchLocation: SearchLocation, didUserChange: boolean) => {
      if (didUserChange) {
        trackEvent(
          'Changed Search Location',
          extractLocationAnalyticsProperties(searchLocation),
        );
      }

      setItem('searchLocation', searchLocation);
      searchStore.setState({
        searchLocation: {
          ...EMPTY_SEARCH_LOCATION,
          ...searchLocation,
          hasGeo: !isLocationEmpty(searchLocation),
        },
      });
    },
    [searchStore],
  );

  const setSearchRadius = useCallback(
    (radius: string | undefined) => {
      return searchStore.setState({ radius });
    },
    [searchStore],
  );

  const clearSearchLocation = useCallback(
    (didUserChange: boolean): void => {
      if (didUserChange) {
        trackEvent('Cleared Search Location');
      }

      setItem('searchLocation', EMPTY_SEARCH_LOCATION);
      searchStore.setState({ searchLocation: EMPTY_SEARCH_LOCATION });
    },
    [searchStore],
  );

  const setSearchFilters = useCallback(
    (filtersById: SearchFiltersById) => {
      searchStore.setState({ filters: filtersById });
    },
    [searchStore],
  );

  const setSearchFiltersByName = useCallback(
    (filtersByName: SearchFiltersByName) => {
      searchStore.setState({ filtersByName });
    },
    [searchStore],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      searchStore.setState({ query, pageIndex: 0 });
    },
    [searchStore],
  );

  const setSearchSort = useCallback(
    (sort: SearchSortType) => {
      searchStore.setState({ sort, pageIndex: 0 });
    },
    [searchStore],
  );

  const setSearchPageIndex = useCallback(
    (pageIndex: number) => {
      searchStore.setState({ pageIndex });
    },
    [searchStore],
  );

  const setSearchFacetCounts = useCallback(
    (facetCounts: SearchFacetCounts) => {
      searchStore.setState({ facetCounts });
    },
    [searchStore],
  );

  const fetchSearchPageResults = useCallback(
    async (pageIndex?: number, withFacetCounts = true) => {
      const filtersByName = { ...getSearchFiltersByName() };
      const filtersById = { ...getSearchFiltersById() };

      const facets = filtersByName
        ? SEARCH_REFINEMENT_NAMES[filtersByName.type]
        : [];

      if (isInEmbedSearchWidget && filtersByName.type === 'VOLOP') {
        if ((filtersByName.source ?? []).length === 0) {
          filtersByName.source = EMBED_SEARCH_WIDGET_VOLOP_SOURCES_ALLOWED;
          filtersById.volopSourceFacet =
            EMBED_SEARCH_WIDGET_VOLOP_SOURCES_ALLOWED;
        }
      }

      searchStore.setState({ isSearching: true });

      const timestamp = new Date().getTime();
      lastSearchTimestamp = timestamp;

      const searchLocation = getSearchLocation();
      const searchRadius = getSearchRadius();

      const facetCountIndexName = ['VOLOP', 'EVENT'].includes(
        filtersByName.type,
      )
        ? 'volopsIndexName'
        : 'siteIndexName';

      const { locationOptions } =
        searchLocation?.hasGeo &&
        searchLocation?.latitude &&
        searchLocation?.longitude
          ? searchAlgoliaOptionsLocationData(searchRadius, searchLocation)
          : {};

      const shouldFetchPromotedListing =
        getSearchFilterType() === 'VOLOP' &&
        (!searchLocation?.countryCode || searchLocation.countryCode === 'US');

      const [{ results }, promotedListing] = await Promise.all([
        algoliaSearchMulti<SearchAlgoliaHit>({
          config: getSearchAlgoliaDefaultConfig(),
          queries: [
            {
              query: getSearchQuery(),
              indexName: searchAlgoliaIndexSwitch({
                filtersById,
                sort: getSearchSort(),
              }),
              options: {
                ...searchAlgoliaOptionsWithSearchLocation({
                  options: searchAlgoliaOptionsSwitch({
                    i18n,
                    filtersById,
                    searchLocation,
                  }),
                  searchLocation,
                  radius: searchRadius,
                }),
                page:
                  typeof pageIndex === 'undefined'
                    ? getSearchPageIndex()
                    : pageIndex,
              },
            },
            ...(withFacetCounts
              ? facets.map((facet) => ({
                  query: getSearchQuery(),
                  options: {
                    facets: [facet],
                    filters: algoliaBuildFilterString({
                      ...filtersByName,
                      [facet]: undefined,
                    }),
                    ...locationOptions,
                  },
                  indexName: configString('algolia', facetCountIndexName),
                  analytics: false,
                }))
              : []),
          ],
        }),
        shouldFetchPromotedListing
          ? searchApiFetchPromotedVolop({
              latitude: searchLocation?.latitude,
              longitude: searchLocation?.longitude,
            })
          : null,
      ]);

      const [searchResults, ...facetResults] = results || [];

      // Merge facet counts
      const facetCounts = facetResults.reduce(
        (acc: SearchFacetCounts, cur: SearchResponse<SearchAlgoliaHit>) => ({
          ...acc,
          ...cur.facets,
        }),
        {},
      );

      // Check if a different search ran afterwards
      if (lastSearchTimestamp === timestamp) {
        const { resultsByPage } = searchStore.getState();
        searchStore.setState({
          facetCounts,
          resultsByPage: {
            ...resultsByPage,
            [searchResults.page ?? 0]: searchResults,
          },
          promotedListing: promotedListing?.listingPromo ?? null,
          isSearching: false,
        });
      }

      return searchResults;
    },
    [
      i18n,
      isInEmbedSearchWidget,
      getSearchFiltersById,
      getSearchFiltersByName,
      getSearchFilterType,
      getSearchLocation,
      getSearchPageIndex,
      getSearchQuery,
      getSearchRadius,
      getSearchSort,
      searchStore,
    ],
  );

  return {
    updateSearchLocation,
    setSearchRadius,
    clearSearchLocation,
    setSearchFilters,
    setSearchFiltersByName,
    setSearchQuery,
    setSearchSort,
    setSearchPageIndex,
    setSearchFacetCounts,
    fetchSearchPageResults,
  };
}
